import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Table, Button, Spinner, Alert, Badge, Modal, Form, Tabs, Tab, ProgressBar } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { fmtRelative, fmtDate } from '../lib/lookups';
import { jobStatusMeta, rowStatusMeta, targetTypeLabel } from '../lib/importMeta';
import { useCounties } from '../hooks/useLocations';

function PayloadCell({ payload, target_type }) {
  if (target_type === 'supervisor_of_elections') {
    return (
      <div>
        <div className="fw-semibold">{payload?.name || <span className="text-muted">(no name)</span>}</div>
        {payload?.title && <div className="text-muted" style={{ fontSize: '0.82rem' }}>{payload.title}</div>}
        <div className="d-flex flex-wrap gap-2 mt-1" style={{ fontSize: '0.78rem' }}>
          {payload?.phone && <Badge bg="light" text="dark"><i className="bi bi-telephone me-1" />{payload.phone}</Badge>}
          {payload?.email && <Badge bg="light" text="dark"><i className="bi bi-envelope me-1" />{payload.email}</Badge>}
          {payload?.website && <Badge bg="light" text="dark"><i className="bi bi-globe me-1" />{payload.website.replace(/^https?:\/\//, '').slice(0, 32)}</Badge>}
        </div>
        {payload?.office_address && (
          <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}><i className="bi bi-geo-alt me-1" />{payload.office_address}</div>
        )}
      </div>
    );
  }
  if (target_type === 'election_dates') {
    const name = payload?.election_name || payload?.name;
    return (
      <div>
        <div className="fw-semibold">{name || <span className="text-muted">(no election name)</span>}</div>
        {payload?.election_type && <div className="text-muted" style={{ fontSize: '0.82rem' }}>{payload.election_type}</div>}
        <div className="d-flex flex-wrap gap-2 mt-1" style={{ fontSize: '0.78rem' }}>
          {payload?.election_date && <Badge bg="primary"><i className="bi bi-calendar-event me-1" />Election: {payload.election_date}</Badge>}
          {payload?.registration_deadline && <Badge bg="light" text="dark">Reg by {payload.registration_deadline}</Badge>}
          {payload?.vote_by_mail_deadline && <Badge bg="light" text="dark">VBM by {payload.vote_by_mail_deadline}</Badge>}
          {(payload?.early_voting_start || payload?.early_voting_end) && (
            <Badge bg="light" text="dark">Early voting {payload?.early_voting_start || '?'} – {payload?.early_voting_end || '?'}</Badge>
          )}
        </div>
      </div>
    );
  }
  return <pre className="mb-0" style={{ fontSize: '0.78rem' }}>{JSON.stringify(payload, null, 2)}</pre>;
}

export default function ImportJobDetailPage() {
  const { stateId, jobId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { counties } = useCounties(stateId);

  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyRowId, setBusyRowId] = useState('');
  const [applying, setApplying] = useState(false);
  const [tab, setTab] = useState('staged');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editCountyId, setEditCountyId] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [showDeleteJob, setShowDeleteJob] = useState(false);
  const [deletingJob, setDeletingJob] = useState(false);
  // Bulk-action state. Selection is per-tab; switching tabs clears it.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const pollRef = useRef(null);

  // Clear bulk selection whenever the active tab changes so a selection made
  // in "Staged" doesn't accidentally carry into "Approved".
  useEffect(() => { setSelectedIds(new Set()); }, [tab]);

  function toggleRow(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAll(rows, on) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => {
        if (r.status === 'applied') return; // never bulk-act on applied rows
        if (on) next.add(r.id); else next.delete(r.id);
      });
      return next;
    });
  }
  function clearSelection() { setSelectedIds(new Set()); }

  function countyName(id) {
    if (!id) return null;
    return counties.find((c) => c.id === id)?.name || null;
  }

  async function refresh() {
    setError('');
    try {
      const [jobRes, rowsRes] = await Promise.all([
        adminApi.get(`/admin/states/${stateId}/import-jobs/${jobId}`),
        adminApi.get(`/admin/states/${stateId}/import-jobs/${jobId}/rows`, { params: { page_size: 500 } }),
      ]);
      setJob(jobRes.data);
      setRows(rowsRes.data.items || []);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [stateId, jobId]);

  // Live polling while the job is running
  useEffect(() => {
    if (!job || job.status !== 'running') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(refresh, 3000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line
  }, [job?.status]);

  const grouped = useMemo(() => {
    const out = { staged: [], approved: [], rejected: [], applied: [], failed_apply: [] };
    rows.forEach((r) => { (out[r.status] || (out[r.status] = [])).push(r); });
    return out;
  }, [rows]);

  async function approveRow(row) {
    setBusyRowId(row.id);
    try {
      await adminApi.post(`/admin/states/${stateId}/import-jobs/${jobId}/rows/${row.id}/approve`);
      toast.success(`Approved "${row.payload?.name || row.id.slice(0, 6)}"`);
      await refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusyRowId(''); }
  }

  async function submitReject() {
    if (!rejectTarget) return;
    setBusyRowId(rejectTarget.id);
    try {
      await adminApi.post(
        `/admin/states/${stateId}/import-jobs/${jobId}/rows/${rejectTarget.id}/reject`,
        { reason: rejectReason || '' }
      );
      toast.success('Rejected');
      setRejectTarget(null);
      setRejectReason('');
      await refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusyRowId(''); }
  }

  async function bulkApprove(ids) {
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const res = await adminApi.post(`/admin/states/${stateId}/import-jobs/${jobId}/rows/bulk`, {
        action: 'approve',
        row_ids: ids,
      });
      const { succeeded, skipped, not_found } = res.data || {};
      const skip_count = (skipped || []).length + (not_found || []).length;
      toast.success(`Approved ${succeeded || 0} row(s)${skip_count ? `, ${skip_count} skipped` : ''}`);
      clearSelection();
      await refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBulkBusy(false); }
  }

  async function bulkReject(ids, reason) {
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const res = await adminApi.post(`/admin/states/${stateId}/import-jobs/${jobId}/rows/bulk`, {
        action: 'reject',
        row_ids: ids,
        reason: reason || '',
      });
      const { succeeded, skipped, not_found } = res.data || {};
      const skip_count = (skipped || []).length + (not_found || []).length;
      toast.success(`Rejected ${succeeded || 0} row(s)${skip_count ? `, ${skip_count} skipped` : ''}`);
      setBulkRejectOpen(false);
      setBulkRejectReason('');
      clearSelection();
      await refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBulkBusy(false); }
  }

  async function applyJob() {
    if (!grouped.approved.length) {
      toast.error('Approve at least one row before applying.');
      return;
    }
    if (!window.confirm(`Apply ${grouped.approved.length} approved row(s) into ${targetTypeLabel(job.target_type)} records?`)) return;
    setApplying(true);
    try {
      const res = await adminApi.post(`/admin/states/${stateId}/import-jobs/${jobId}/apply`);
      const data = res.data || {};
      toast.success(`Applied ${data.applied} row(s)${data.failures?.length ? `, ${data.failures.length} failed` : ''}`);
      await refresh();
      setTab('applied');
    } catch (e) { toast.error(extractError(e)); }
    finally { setApplying(false); }
  }

  function openEdit(row) {
    setEditTarget(row);
    setEditForm({ ...(row.payload || {}) });
    setEditCountyId(row.county_id || '');
  }

  async function saveEdit() {
    if (!editTarget) return;
    const isElectionDates = editTarget.target_type === 'election_dates';
    if (isElectionDates) {
      const en = (editForm.election_name || editForm.name || '').trim();
      if (!en) {
        toast.error('Election name is required.');
        return;
      }
    } else if (!(editForm.name || '').trim()) {
      toast.error('Name is required.');
      return;
    }
    setSavingEdit(true);
    try {
      await adminApi.put(
        `/admin/states/${stateId}/import-jobs/${jobId}/rows/${editTarget.id}`,
        { county_id: editCountyId || '', payload: editForm }
      );
      toast.success('Row updated');
      setEditTarget(null);
      setEditForm({});
      setEditCountyId('');
      await refresh();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingEdit(false); }
  }

  async function deleteJob() {
    setDeletingJob(true);
    try {
      const res = await adminApi.delete(`/admin/states/${stateId}/import-jobs/${jobId}`);
      const sourceId = res?.data?.data_source_id || job?.data_source_id;
      toast.success('Import job deleted');
      // Redirect back to the Data Source edit page if we know it, otherwise the source list
      if (sourceId) {
        navigate(`/admin/states/${stateId}/data-sources/${sourceId}`);
      } else {
        navigate(`/admin/states/${stateId}/data-sources`);
      }
    } catch (e) { toast.error(extractError(e)); setShowDeleteJob(false); }
    finally { setDeletingJob(false); }
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Import job" />
        <div className="text-center p-5"><Spinner animation="border" /></div>
      </>
    );
  }

  if (!job) {
    return (
      <>
        <PageHeader title="Import job" />
        <Alert variant="danger">{error || 'Job not found'}</Alert>
      </>
    );
  }

  const meta = jobStatusMeta(job.status);

  function renderRows(list, mode) {
    if (list.length === 0) {
      return (
        <div className="empty-state">
          <i className="bi bi-inbox" />
          <div>No rows in this bucket.</div>
        </div>
      );
    }
    const bulkEnabled = mode === 'staged' || mode === 'approved' || mode === 'rejected';
    // Rows in this bucket eligible for bulk select (everything except already-applied)
    const selectableRows = list.filter((r) => r.status !== 'applied');
    const selectedInTab = selectableRows.filter((r) => selectedIds.has(r.id)).length;
    const allChecked = selectableRows.length > 0 && selectedInTab === selectableRows.length;
    const someChecked = selectedInTab > 0 && !allChecked;
    const selectedIdsArray = selectableRows.filter((r) => selectedIds.has(r.id)).map((r) => r.id);
    return (
      <>
        {bulkEnabled && selectedInTab > 0 && (
          <div
            className="d-flex align-items-center gap-2 p-2 mb-2 rounded"
            style={{ background: 'var(--bs-light, #f8f9fa)', border: '1px solid var(--bs-border-color, #dee2e6)' }}
            data-testid={`bulk-action-bar-${mode}`}
          >
            <strong>{selectedInTab} selected</strong>
            <span className="text-muted" style={{ fontSize: '0.82rem' }}>
              of {selectableRows.length} in this bucket
            </span>
            <div className="ms-auto d-flex gap-2">
              {mode !== 'approved' && (
                <Button
                  size="sm"
                  variant="success"
                  disabled={bulkBusy}
                  onClick={() => bulkApprove(selectedIdsArray)}
                  data-testid="bulk-approve-button"
                >
                  {bulkBusy ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-check-lg me-1" /> Approve selected</>}
                </Button>
              )}
              {mode !== 'rejected' && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={bulkBusy}
                  onClick={() => { setBulkRejectReason(''); setBulkRejectOpen(true); }}
                  data-testid="bulk-reject-button"
                >
                  {bulkBusy ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-x-lg me-1" /> Reject selected</>}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={bulkBusy}
                onClick={clearSelection}
                data-testid="bulk-clear-selection-button"
              >
                Clear
              </Button>
            </div>
          </div>
        )}
        <Table hover responsive size="sm" className="mb-0" data-testid={`import-rows-table-${mode}`}>
        <thead>
          <tr>
            {bulkEnabled && (
              <th style={{ width: 34 }} className="text-center">
                <Form.Check
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked; }}
                  onChange={(e) => selectAll(selectableRows, e.target.checked)}
                  disabled={selectableRows.length === 0}
                  aria-label={allChecked ? 'Deselect all' : 'Select all'}
                  data-testid={`bulk-select-all-${mode}`}
                />
              </th>
            )}
            <th style={{ width: '40%' }}>Extracted record</th>
            <th>County</th>
            <th>Bio / Notes</th>
            <th>Status</th>
            {bulkEnabled && <th className="text-end">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {list.map((row) => {
            const rmeta = rowStatusMeta(row.status);
            const isBusy = busyRowId === row.id;
            const cName = countyName(row.county_id);
            const checked = selectedIds.has(row.id);
            const canSelect = row.status !== 'applied';
            return (
              <tr key={row.id} data-testid={`import-row-${row.id}`} className={checked ? 'table-active' : ''}>
                {bulkEnabled && (
                  <td className="text-center align-middle">
                    {canSelect ? (
                      <Form.Check
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRow(row.id)}
                        aria-label={`Select ${row.payload?.name || row.id.slice(0, 8)}`}
                        data-testid={`bulk-select-row-${row.id}`}
                      />
                    ) : null}
                  </td>
                )}
                <td><PayloadCell payload={row.payload} target_type={row.target_type} /></td>
                <td style={{ fontSize: '0.82rem' }}>
                  {cName ? (
                    <span><i className="bi bi-geo-alt me-1 text-muted" />{cName}</span>
                  ) : (
                    <Badge bg="warning" text="dark" title={row.warnings?.[0] || 'No county mapped'}>unmapped</Badge>
                  )}
                </td>
                <td style={{ fontSize: '0.82rem', maxWidth: 320 }}>
                  {row.payload?.bio && <div>{row.payload.bio.slice(0, 220)}{row.payload.bio.length > 220 ? '…' : ''}</div>}
                  {row.payload?.notes && <div className="text-muted mt-1">{row.payload.notes.slice(0, 160)}</div>}
                  {row.reject_reason && <div className="text-danger mt-1"><strong>Reject reason:</strong> {row.reject_reason}</div>}
                </td>
                <td><span className={`status-badge ${rmeta.badge}`}>{rmeta.label}</span></td>
                {bulkEnabled && (
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <Button size="sm" variant="outline-secondary" disabled={isBusy} onClick={() => openEdit(row)} data-testid={`import-row-edit-${row.id}`}>
                        <i className="bi bi-pencil" /> Edit
                      </Button>
                      {row.status !== 'approved' && row.status !== 'applied' && (
                        <Button size="sm" variant="outline-success" disabled={isBusy} onClick={() => approveRow(row)} data-testid={`import-row-approve-${row.id}`}>
                          <i className="bi bi-check-lg" /> Approve
                        </Button>
                      )}
                      {row.status !== 'rejected' && row.status !== 'applied' && (
                        <Button size="sm" variant="outline-danger" disabled={isBusy} onClick={() => { setRejectTarget(row); setRejectReason(''); }} data-testid={`import-row-reject-${row.id}`}>
                          <i className="bi bi-x-lg" /> Reject
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={`Import Job · ${job.data_source_name || job.data_source_id.slice(0, 8)}`}
        subtitle={`Started ${job.started_at ? fmtDate(job.started_at) : 'n/a'} · target: ${targetTypeLabel(job.target_type)}`}
        breadcrumb={
          <span>
            <Link to="/admin/states" className="text-decoration-none">States</Link>
            {' / '}<Link to={`/admin/states/${stateId}/data-management`} className="text-decoration-none">Data Management</Link>
            {' / '}<Link to={`/admin/states/${stateId}/import-jobs`} className="text-decoration-none">Import Jobs</Link>
            {' / '}{job.id.slice(0, 8)}…
          </span>
        }
        actions={
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => navigate(`/admin/states/${stateId}/import-jobs`)}>Back</Button>
            <Button variant="primary" onClick={applyJob} disabled={applying || !grouped.approved.length} data-testid="import-job-apply-button">
              {applying ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-lightning-charge me-1" /> Apply approved ({grouped.approved.length})</>}
            </Button>
            <Button variant="outline-danger" onClick={() => setShowDeleteJob(true)} disabled={job.status === 'running'} data-testid="import-job-delete-button">
              <i className="bi bi-trash me-1" /> Delete job
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        {job.status === 'running' && (
          <Alert variant="info" className="d-flex align-items-center gap-3">
            <Spinner animation="border" size="sm" />
            <div>
              <strong>Import is running…</strong>{' '}
              {job.parsing_method === 'ai_crawl'
                ? `Crawling sub-pages (${job.pages_succeeded || 0}/${job.pages_attempted || '?'} so far). This page refreshes every 3 s.`
                : 'Fetching and extracting. This page refreshes every 3 s.'}
            </div>
            {job.parsing_method === 'ai_crawl' && job.pages_attempted > 0 && (
              <div style={{ minWidth: 220 }}>
                <ProgressBar now={(job.pages_succeeded / Math.max(1, job.pages_attempted)) * 100} label={`${job.pages_succeeded}/${job.pages_attempted}`} />
              </div>
            )}
          </Alert>
        )}

        <Row className="g-3 mb-3">
          <Col xs={12} md={4}>
            <Card>
              <Card.Body>
                <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase' }}>Status</div>
                <div className="mt-1"><span className={`status-badge ${meta.badge}`}>{meta.label}</span></div>
                {job.error_message && <div className="text-danger mt-2" style={{ fontSize: '0.85rem' }}>{job.error_message}</div>}
                <div className="mt-3 text-muted" style={{ fontSize: '0.78rem' }}>
                  <div>HTTP {job.fetch_http_status ?? '—'} · {job.fetch_bytes ? `${(job.fetch_bytes / 1024).toFixed(1)} KB` : '—'}</div>
                  <div>Model: <code>{job.llm_model || '—'}</code></div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={8}>
            <Card>
              <Card.Body>
                <Row className="g-3 text-center">
                  <Col><div className="text-muted" style={{ fontSize: '0.78rem' }}>Extracted</div><div className="fs-4 fw-bold">{job.items_extracted_count}</div></Col>
                  <Col><div className="text-muted" style={{ fontSize: '0.78rem' }}>Staged</div><div className="fs-4 fw-bold">{job.items_staged_count}</div></Col>
                  <Col><div className="text-muted" style={{ fontSize: '0.78rem' }}>Approved</div><div className="fs-4 fw-bold text-info">{job.items_approved_count}</div></Col>
                  <Col><div className="text-muted" style={{ fontSize: '0.78rem' }}>Rejected</div><div className="fs-4 fw-bold text-secondary">{job.items_rejected_count}</div></Col>
                  <Col><div className="text-muted" style={{ fontSize: '0.78rem' }}>Applied</div><div className="fs-4 fw-bold text-success">{job.items_applied_count}</div></Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Body className="pb-0">
            <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3" data-testid="import-job-tabs">
              <Tab eventKey="staged" title={`Staged (${grouped.staged.length})`}>{renderRows(grouped.staged, 'staged')}</Tab>
              <Tab eventKey="approved" title={`Approved (${grouped.approved.length})`}>{renderRows(grouped.approved, 'approved')}</Tab>
              <Tab eventKey="rejected" title={`Rejected (${grouped.rejected.length})`}>{renderRows(grouped.rejected, 'rejected')}</Tab>
              <Tab eventKey="applied" title={`Applied (${grouped.applied.length})`}>{renderRows(grouped.applied, 'applied')}</Tab>
              {grouped.failed_apply.length > 0 && (
                <Tab eventKey="failed_apply" title={`Failed apply (${grouped.failed_apply.length})`}>{renderRows(grouped.failed_apply, 'failed_apply')}</Tab>
              )}
            </Tabs>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showDeleteJob} onHide={() => setShowDeleteJob(false)}>
        <Modal.Header closeButton><Modal.Title>Delete import job?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>
            This will permanently delete the job and <strong>all of its staged rows</strong>.
            {' '}
            {job.items_applied_count > 0 && (
              <>
                The <strong>{job.items_applied_count}</strong> row{job.items_applied_count === 1 ? '' : 's'} already <em>applied</em> will <strong>not</strong> be removed —
                they live in the {targetTypeLabel(job.target_type)} table and can be deleted there if needed.
              </>
            )}
          </p>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            You will be redirected back to the Data Source page.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteJob(false)} disabled={deletingJob}>Cancel</Button>
          <Button variant="danger" onClick={deleteJob} disabled={deletingJob} data-testid="import-job-delete-confirm-button">
            {deletingJob ? <Spinner size="sm" animation="border" /> : 'Delete job'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!rejectTarget} onHide={() => setRejectTarget(null)}>
        <Modal.Header closeButton><Modal.Title>Reject row</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="mb-2">Reject extracted record <strong>{rejectTarget?.payload?.name || '—'}</strong>?</p>
          <Form.Group>
            <Form.Label>Reason (optional)</Form.Label>
            <Form.Control as="textarea" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="import-row-reject-reason-input" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={submitReject} disabled={!!busyRowId} data-testid="import-row-reject-confirm-button">
            {busyRowId ? <Spinner size="sm" animation="border" /> : 'Reject'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={bulkRejectOpen} onHide={() => !bulkBusy && setBulkRejectOpen(false)}>
        <Modal.Header closeButton><Modal.Title>Reject selected rows</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Reject <strong>{selectedIds.size}</strong> selected row{selectedIds.size === 1 ? '' : 's'}?
            {' '}Rows that are already <em>applied</em> will be skipped.
          </p>
          <Form.Group>
            <Form.Label>Reason (optional, applied to all)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              data-testid="bulk-reject-reason-input"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBulkRejectOpen(false)} disabled={bulkBusy}>Cancel</Button>
          <Button
            variant="danger"
            onClick={() => bulkReject([...selectedIds], bulkRejectReason)}
            disabled={bulkBusy}
            data-testid="bulk-reject-confirm-button"
          >
            {bulkBusy ? <Spinner size="sm" animation="border" /> : `Reject ${selectedIds.size}`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!editTarget} onHide={() => setEditTarget(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Edit staged record
            {editTarget && (
              <span className="text-muted ms-2" style={{ fontSize: '0.85rem', fontWeight: 400 }}>
                · {targetTypeLabel(editTarget.target_type)}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label>County (where this record will land when applied)</Form.Label>
                <Form.Select
                  value={editCountyId}
                  onChange={(e) => setEditCountyId(e.target.value)}
                  data-testid="import-row-edit-county-select"
                >
                  <option value="">— Unmapped (cannot apply) —</option>
                  {counties.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.county_code ? ` (${c.county_code})` : ''}</option>
                  ))}
                </Form.Select>
                <Form.Text>
                  {editTarget?.target_type === 'election_dates'
                    ? 'Election dates always live under a county. For truly statewide elections, pick any county or duplicate the row per affected county.'
                    : 'For AI-Crawl rows that couldn’t be auto-matched, pick the correct county here.'}
                </Form.Text>
              </Form.Group>
            </Col>

            {editTarget?.target_type === 'election_dates' ? (
              <>
                <Col xs={12} md={8}>
                  <Form.Group>
                    <Form.Label>Election name *</Form.Label>
                    <Form.Control
                      value={editForm.election_name || editForm.name || ''}
                      onChange={(e) => setEditForm({ ...editForm, election_name: e.target.value, name: e.target.value })}
                      placeholder="e.g., 2026 General Election"
                      data-testid="import-row-edit-election-name-input"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Type</Form.Label>
                    <Form.Control
                      value={editForm.election_type || ''}
                      onChange={(e) => setEditForm({ ...editForm, election_type: e.target.value })}
                      placeholder="Primary / General / Special"
                      data-testid="import-row-edit-election-type-input"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Election date</Form.Label>
                    <Form.Control
                      value={editForm.election_date || ''}
                      onChange={(e) => setEditForm({ ...editForm, election_date: e.target.value })}
                      placeholder="YYYY-MM-DD"
                      data-testid="import-row-edit-election-date-input"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Registration deadline</Form.Label>
                    <Form.Control
                      value={editForm.registration_deadline || ''}
                      onChange={(e) => setEditForm({ ...editForm, registration_deadline: e.target.value })}
                      placeholder="YYYY-MM-DD"
                      data-testid="import-row-edit-registration-deadline-input"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label>Vote-by-mail deadline</Form.Label>
                    <Form.Control
                      value={editForm.vote_by_mail_deadline || ''}
                      onChange={(e) => setEditForm({ ...editForm, vote_by_mail_deadline: e.target.value })}
                      placeholder="YYYY-MM-DD"
                      data-testid="import-row-edit-vbm-deadline-input"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Early voting starts</Form.Label>
                    <Form.Control
                      value={editForm.early_voting_start || ''}
                      onChange={(e) => setEditForm({ ...editForm, early_voting_start: e.target.value })}
                      placeholder="YYYY-MM-DD"
                      data-testid="import-row-edit-ev-start-input"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Early voting ends</Form.Label>
                    <Form.Control
                      value={editForm.early_voting_end || ''}
                      onChange={(e) => setEditForm({ ...editForm, early_voting_end: e.target.value })}
                      placeholder="YYYY-MM-DD"
                      data-testid="import-row-edit-ev-end-input"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control as="textarea" rows={2}
                      value={editForm.notes || ''}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      data-testid="import-row-edit-notes-input"
                    />
                  </Form.Group>
                </Col>
              </>
            ) : (
              <>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Name *</Form.Label>
                    <Form.Control value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="import-row-edit-name-input" />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Title</Form.Label>
                    <Form.Control value={editForm.title || ''} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="import-row-edit-title-input" />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="import-row-edit-phone-input" />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="import-row-edit-email-input" />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Website</Form.Label>
                    <Form.Control value={editForm.website || ''} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} data-testid="import-row-edit-website-input" />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Office address</Form.Label>
                    <Form.Control as="textarea" rows={2} value={editForm.office_address || ''} onChange={(e) => setEditForm({ ...editForm, office_address: e.target.value })} data-testid="import-row-edit-office-input" />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Mailing address</Form.Label>
                    <Form.Control as="textarea" rows={2} value={editForm.mailing_address || ''} onChange={(e) => setEditForm({ ...editForm, mailing_address: e.target.value })} data-testid="import-row-edit-mailing-input" />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Hours</Form.Label>
                    <Form.Control value={editForm.hours || ''} onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })} data-testid="import-row-edit-hours-input" />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label>Photo URL</Form.Label>
                    <Form.Control value={editForm.photo_url || ''} onChange={(e) => setEditForm({ ...editForm, photo_url: e.target.value })} data-testid="import-row-edit-photo-input" />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Bio</Form.Label>
                    <Form.Control as="textarea" rows={3} value={editForm.bio || ''} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} data-testid="import-row-edit-bio-input" />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Notes</Form.Label>
                    <Form.Control as="textarea" rows={2} value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="import-row-edit-notes-input" />
                  </Form.Group>
                </Col>
              </>
            )}

            <Col xs={12}>
              <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                Source: <a href={editForm.source_url} target="_blank" rel="noreferrer">{editForm.source_url || '—'}</a>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="primary" onClick={saveEdit} disabled={savingEdit} data-testid="import-row-edit-save-button">
            {savingEdit ? <Spinner size="sm" animation="border" /> : 'Save changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
